import React, { useState, useEffect, useCallback } from 'react';
import { Search, Mail, Loader2 } from 'lucide-react';
import api, { TeamMember } from '../services/api';

const TeamPage: React.FC = () => {
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');

  const fetchTeamMembers = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.getTeamMembers({
        role: selectedRole !== 'all' ? selectedRole : undefined,
        search: searchQuery || undefined
      });
      setTeamMembers(response.results);
      setError(null);
    } catch (err) {
      setError('Failed to load team members');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [selectedRole, searchQuery]);

  useEffect(() => {
    fetchTeamMembers();
  }, [fetchTeamMembers]);

  const getRoleDisplay = (role: string) => {
    const roleMap: Record<string, string> = {
      'admin': 'Administrator',
      'manager': 'Account Manager',
      'specialist': 'Marketing Specialist',
      'developer': 'Developer',
      'freelancer': 'Freelancer'
    };
    return roleMap[role] || role;
  };

  if (loading && teamMembers.length === 0) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-black flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-black">
      {/* Header - Compact */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Team Management</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">AI and Marketing team members</p>
          </div>
        </div>
      </div>

      {/* Filters - Compact */}
      <div className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search team members..."
                className="input pl-7"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <select className="select" value={selectedRole} onChange={(e) => setSelectedRole(e.target.value)}>
              <option value="all">All Roles</option>
              <option value="admin">Administrator</option>
              <option value="manager">Account Manager</option>
              <option value="specialist">Marketing Specialist</option>
              <option value="developer">Developer</option>
              <option value="freelancer">Freelancer</option>
            </select>
          </div>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="max-w-7xl mx-auto px-3 py-2">
          <div className="bg-status-error/10 border border-status-error/30 rounded p-2 text-xs text-status-error">{error}</div>
        </div>
      )}

      {/* Team Grid - Compact */}
      <div className="max-w-7xl mx-auto px-3 py-3">
        {teamMembers.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-xs text-gray-500 dark:text-gray-400">No team members found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-2">
            {teamMembers.map((member) => (
              <div key={member.id} className="panel">
                <div className="p-3">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">{member.full_name}</h3>
                        <span className={`status-badge ${member.is_active_agent ? 'status-active' : 'status-paused'}`}>
                          {member.is_active_agent ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{getRoleDisplay(member.role)}</p>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-2 gap-x-3 gap-y-1 mb-2 text-xs">
                    <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                      <Mail className="w-3 h-3 text-gray-400" />
                      <span className="truncate">{member.email}</span>
                    </div>
                    {member.phone_extension && (
                      <div className="text-gray-600 dark:text-gray-300">Ext: {member.phone_extension}</div>
                    )}
                  </div>

                  {/* Skills */}
                  {member.skills && member.skills.length > 0 && (
                    <div className="mb-2">
                      <div className="flex flex-wrap gap-1">
                        {member.skills.map((skill, index) => (
                          <span key={index} className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                            {skill}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Performance */}
                  <div className="border-t border-light-border dark:border-dark-border pt-2">
                    <div className="grid grid-cols-2 gap-2">
                      <div className="text-center">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{member.active_projects}</p>
                        <p className="text-xs text-gray-500">Active</p>
                      </div>
                      <div className="text-center">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white">{member.projects_completed}</p>
                        <p className="text-xs text-gray-500">Completed</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamPage;
